import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { StepDuration } from "../../../../types/onboardingAnalytics";
import { OnboardingState } from "../../../../types/onboarding";
import { stateLabel } from "../../../../utils/onboardingStates";
import { formatDays } from "../../../../utils/analyticsFormat";

interface TimePerStepChartProps {
  steps: StepDuration[];
}

/**
 * Average days per recorded step.
 *
 * The API sends each step as ONE string — "OCPP_CREDENTIALS_SENT →
 * WELCOME_BOX_SENT" — which is two raw identifiers in a display field. The
 * service splits it at the boundary; this renders the halves as two labelled
 * states so nothing raw reaches the screen (FR-031).
 *
 * The section carries a caveat because the endpoint keeps only pairs that are
 * ADJACENT in the backend's pipeline order, and several common journeys are not:
 * promotion routinely skips a state, and ESIGN_AWAITED sits between the draft
 * and the sent contract, so neither signing path forms an adjacent pair. The
 * result is a real view of a partial set, which is worth saying out loud rather
 * than letting an agent conclude that signing takes no time.
 */
const TimePerStepChart: React.FC<TimePerStepChartProps> = ({ steps }) => {
  const stepBody = (step: StepDuration) => (
    <span>
      {stateLabel(step.from as OnboardingState)}
      <i className="pi pi-arrow-right mx-2 text-xs text-500" />
      {stateLabel(step.to as OnboardingState)}
    </span>
  );

  const daysBody = (step: StepDuration) => formatDays(step.avgDays) ?? "—";

  return (
    <DataTable
      value={steps}
      dataKey="from"
      className="p-datatable-sm"
      aria-label="Average time per step"
    >
      <Column header="Step" body={stepBody} />
      <Column
        header="Average"
        body={daysBody}
        style={{ width: "9rem" }}
      />
    </DataTable>
  );
};

export default TimePerStepChart;
